import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useClient } from './ClientContext';
import { useApiClient } from '../api/apiClient';

const FindingsContext = createContext(null);

const CACHE_TTL = 120000; // 2 minutes

export function FindingsProvider({ children }) {
  const { clientId } = useClient();
  const location = useLocation();
  const api = useApiClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [includeSuppressed, setIncludeSuppressed] = useState(false);
  const lastClientId = useRef(null);
  const lastParams = useRef('');
  const fetchId = useRef(0);
  const isDashboardRoute = location.pathname === '/client/dashboard';
  const [deferredDashboardFetchReady, setDeferredDashboardFetchReady] = useState(false);

  useEffect(() => {
    if (!isDashboardRoute) {
      setDeferredDashboardFetchReady(true);
      return undefined;
    }

    setDeferredDashboardFetchReady(false);
    const timer = window.setTimeout(() => setDeferredDashboardFetchReady(true), 2500);
    return () => window.clearTimeout(timer);
  }, [isDashboardRoute, clientId]);

  const fetchFindings = useCallback(() => {
    if (!clientId) return;
    const id = ++fetchId.current;
    lastClientId.current = clientId;
    const params = new URLSearchParams({ pageSize: '500' });
    if (includeSuppressed) params.set('includeSuppressed', 'true');
    const paramStr = params.toString();
    lastParams.current = paramStr;

    // Check sessionStorage cache first
    const cacheKey = `findings-${clientId}-${paramStr}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data: cachedData, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          setData(cachedData);
          setLoading(false);
          setLoadingProgress('');
          return;
        }
      }
    } catch (_) {}

    setLoading(true);
    setError(null);
    setLoadingProgress('Loading findings...');

    // Timeout for slow responses
    const slowTimer = setTimeout(() => {
      setLoadingProgress('Taking longer than expected...');
    }, 15000);

    api.get(`/clients/${clientId}/findings?${paramStr}`)
      .then(result => {
        clearTimeout(slowTimer);
        if (id !== fetchId.current) return;
        const findings = Array.isArray(result?.findings)
          ? result.findings
          : Array.isArray(result) ? result : [];
        setData(findings);
        setLoadingProgress('');
        // Cache to sessionStorage
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ data: findings, ts: Date.now() }));
        } catch (_) {}
      })
      .catch(err => {
        clearTimeout(slowTimer);
        if (id !== fetchId.current) return;
        setError(err.message);
        setLoadingProgress('');
      })
      .finally(() => {
        clearTimeout(slowTimer);
        if (id === fetchId.current) setLoading(false);
      });
  }, [clientId, api, includeSuppressed]);

  useEffect(() => {
    if (!clientId) { setData(null); return; }
    if (isDashboardRoute && !deferredDashboardFetchReady) return;
    const params = new URLSearchParams({ pageSize: '500' });
    if (includeSuppressed) params.set('includeSuppressed', 'true');
    if (clientId === lastClientId.current && params.toString() === lastParams.current && data) return;
    fetchFindings();
  }, [clientId, includeSuppressed, isDashboardRoute, deferredDashboardFetchReady]);

  const refetch = useCallback(() => {
    lastClientId.current = null;
    // Clear cache for this client
    if (clientId) {
      try {
        Object.keys(sessionStorage)
          .filter(k => k.startsWith(`findings-${clientId}`))
          .forEach(k => sessionStorage.removeItem(k));
      } catch (_) {}
    }
    fetchFindings();
  }, [fetchFindings, clientId]);

  // Optimistic remove — hide a finding from local cache immediately
  const removeFinding = useCallback((findingId) => {
    setData(prev => prev ? prev.filter(f => (f.findingId || f.id) !== findingId) : prev);
  }, []);

  return (
    <FindingsContext.Provider value={{
      findings: data ?? [],
      loading,
      error,
      loadingProgress,
      refetch,
      removeFinding,
      includeSuppressed,
      setIncludeSuppressed,
    }}>
      {children}
    </FindingsContext.Provider>
  );
}

export function useAllFindings() {
  const ctx = useContext(FindingsContext);
  if (!ctx) throw new Error('useAllFindings must be used within FindingsProvider');
  return ctx;
}
